// src/bobs/components/BobsWorkspaceContainer.tsx
import { observer } from "mobx-react";
import React, { useContext, useEffect } from "react";
import { Loader, Stack, Text, Container } from "@mantine/core";
import { BobsWorkspace, BobsWorkspaceContext } from "../stores/BobsWorkspace";
import { BOB_MODALS, BOB_PAGES } from "../helpers/constants";
import Bob from "./bob";
import { startBobAccountsActionCable } from "../helpers/actionCable";
import EventsDrawer from "./eventsDrawer";
import YouHaveBeenDisconnected from "./details/youHaveBeenDisconnected";
import BobIdleTimer from "./details/bobIdleTimer";
import WorkspaceBackground from "./details/workspaceBackground"; // glow for HOME
import AppWorkspaceBackground from "./details/workspaceBackgroundApp"; // darker for others
import { BrowserClient } from "@sentry/react";
import BrowserNotifications from "./BrowserNotifications";

const BobsWorkspaceContainer: React.FC = observer(() => {
  const bobsWorkspace = useContext<BobsWorkspace>(BobsWorkspaceContext);

  useEffect(() => {
    bobsWorkspace.hydrateDataFromPage();
    startBobAccountsActionCable(bobsWorkspace);

    const params = new URLSearchParams(window.location.search);
    if (params.get("get_nwc_codes") === "true" && bobsWorkspace.selectedBob) {
      bobsWorkspace.selectedBob.setOpenModal(BOB_MODALS.NWC_ENTRY);
    }
    // /w?hold=<uuid> from a hold invoice push or email: select the vault that
    // owns it and open its decision modal
    const holdUuid = params.get("hold");
    if (holdUuid) {
      bobsWorkspace.openHoldInvoiceDeepLink(holdUuid);
    }

    // re-open the hold decision modal while a payment waits and the user has
    // not touched it for 10 minutes
    const renag = setInterval(() => bobsWorkspace.maybeRenagHoldDecision(), 15_000);
    return () => clearInterval(renag);
  }, []);

  const wantsGlow = bobsWorkspace.selectedBob?.openPage === BOB_PAGES.HOME;

  const inner = (
    <>
      <BobIdleTimer bobsWorkspace={bobsWorkspace} />

      <Container size={800} px="md">
        <Stack>
          {bobsWorkspace.userHasGoneIdle && (
            <YouHaveBeenDisconnected bob={bobsWorkspace.selectedBob} />
          )}

          {bobsWorkspace.hasLoaded &&
            !bobsWorkspace.userHasGoneIdle &&
            bobsWorkspace.selectedBob && (
              <>
                <BrowserNotifications bobsWorkspace={bobsWorkspace} />
                <Bob bobsWorkspace={bobsWorkspace} />
              </>
            )}

          {!bobsWorkspace.hasLoaded && !bobsWorkspace.userHasGoneIdle && (
            <>
              <Loader />
              <Text>Loading…</Text>
            </>
          )}
        </Stack>
      </Container>

      <EventsDrawer bobsWorkspace={bobsWorkspace} />
    </>
  );

  return wantsGlow ? (
    <WorkspaceBackground wantsGlow>{inner}</WorkspaceBackground>
  ) : (
    <AppWorkspaceBackground>{inner}</AppWorkspaceBackground>
  );
});

export default BobsWorkspaceContainer;
